import { Archive, Flag, PenLine, Tag, UserPlus, Wand2 } from "lucide-react"

import type { FrontendTriggerDefinition } from "../../types"

export const householdTriggers: FrontendTriggerDefinition[] = [
  {
    key: "trigger__household_created",
    label: "Household created",
    description: "Triggers when a new household is created",
    icon: UserPlus,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "household_type",
        label: "Household type",
        inputType: "radio",
        required: true,
        options: [
          { value: "Prospect", label: "Prospect" },
          { value: "Client", label: "Client" },
        ],
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__household_updated",
    label: "Attribute changed",
    description: "Triggers when a household attribute or custom field changes",
    icon: PenLine,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "household_type",
        label: "Household type",
        inputType: "radio",
        required: true,
        options: [
          { value: "Prospect", label: "Prospect" },
          { value: "Client", label: "Client" },
          { value: "Household", label: "All households" },
        ],
      },
      {
        key: "watched_attributes",
        label: "When any of these change...",
        inputType: "multi_select",
        required: true,
        options: "dynamic_household_attributes",
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__household_tag_added",
    label: "Tag added",
    description: "Triggers when a tag is added to a household",
    icon: Tag,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "tag_ids",
        label: "When the new tag is...",
        inputType: "multi_select",
        required: true,
        options: "dynamic_tags",
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__household_sequence_completed",
    label: "Completed sequence",
    description: "Triggers when a household completes or exits a sequence",
    icon: Flag,
    providesSubjectsByInputs: [
      {
        field: "household_type",
        values: {
          Client: ["household"],
          Prospect: ["household"],
        },
      },
    ],
    inputFields: [
      {
        key: "household_type",
        label: "Record type",
        inputType: "radio",
        required: true,
        options: [
          { value: "Client", label: "Client" },
          { value: "Prospect", label: "Prospect" },
        ],
      },
      {
        key: "sequence_ids",
        label: "Sequences",
        inputType: "multi_select",
        required: false,
        options: "dynamic_sequences",
      },
      {
        key: "finish_reasons",
        label: "Finish reasons",
        inputType: "multi_select",
        required: false,
        options: [
          { value: "completed", label: "Completed all steps" },
          { value: "email_reply_received", label: "Email reply received" },
          { value: "meeting_scheduled", label: "Meeting scheduled" },
          { value: "manual_exit", label: "Manually exited" },
          { value: "unsubscribed", label: "Unsubscribed" },
          { value: "inbound_call_received", label: "Inbound call received" },
        ],
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
        visibleWhen: { field: "household_type", values: ["Client", "Prospect"] },
      },
    ],
  },
  {
    key: "trigger__prospect_promoted_to_client",
    label: "Prospect promoted to client",
    description: "Triggers when a prospect is promoted to a client",
    icon: Tag,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__client_archived_to_past_client",
    label: "Client archived to past client",
    description: "Triggers when a client is archived to a past client",
    icon: Archive,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__household_magic_button",
    label: "Magic button",
    description: "Triggers when a magic button is pressed for a household",
    icon: Wand2,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "household_type",
        label: "Household type",
        inputType: "select",
        required: true,
        options: [
          { value: "Prospect", label: "Prospect" },
          { value: "Client", label: "Client" },
          { value: "Household", label: "All households" },
        ],
      },
      {
        key: "title",
        label: "Button title",
        inputType: "text",
        required: true,
        placeholder: "e.g., Request review",
      },
      {
        key: "description",
        label: "Description",
        inputType: "textarea",
        required: true,
        placeholder: "What does this button do?",
      },
      {
        key: "icon_color",
        label: "Icon & color",
        inputType: "icon_color_picker",
        required: true,
        chatDefaultValue: { icon: "wand", color: "#0EA5E9" },
        iconOptions: [
          "wand",
          "sparkles",
          "zap",
          "brain",
          "bot",
          "dollar",
          "coins",
          "banknote",
          "piggy-bank",
          "landmark",
          "chart-line",
          "chart-pie",
          "briefcase",
          "phone",
          "mail",
          "send",
          "calendar",
          "clock",
          "bell",
          "document",
          "household",
          "handshake",
          "star",
          "check",
        ],
        colorFormat: "hex",
      },
    ],
  },
]
